import { useInfiniteQuery } from "@tanstack/react-query";
import type {
  AgentInterrupt,
  AgentToolEndEvent,
  AgentToolStartEvent,
} from "../types/ai_agent";
import type { ChatInterrupt, ChatMessage, ChatTool } from "../types/chat";

function joinUrl(baseUrl: string | undefined, path: string) {
  if (!baseUrl) {
    return path;
  }

  const trimmed = baseUrl.replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;

  return `${trimmed}${p}`;
}

function mapApiRole(role: unknown) {
  switch (role) {
    case "user":
    case "human":
      return "user";
    case "ai":
    case "assistant":
      return "assistant";
    case "tool":
      return "tool";
    case "interrupt":
      return "interrupt";
    case "system":
      return "system";
    default:
      return "assistant";
  }
}

function parseJsonContent(content: unknown) {
  if (typeof content !== "string" || content.length === 0) {
    return null;
  }

  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === "object" && !Array.isArray(x);
}

function isAgentInterrupt(x: unknown): x is AgentInterrupt {
  return isRecord(x) &&
    Array.isArray(x.action_requests) &&
    Array.isArray(x.review_configs);
}

function parseInterrupts(content: unknown): AgentInterrupt[] {
  const parsed = parseJsonContent(content);

  if (isAgentInterrupt(parsed)) {
    return [parsed];
  }

  if (!isRecord(parsed)) {
    return [];
  }

  if (isAgentInterrupt(parsed.interrupt)) {
    return [parsed.interrupt];
  }

  const data = isRecord(parsed.data) ? parsed.data : null;
  const interrupts = Array.isArray(parsed.interrupts)
    ? parsed.interrupts
    : data && Array.isArray(data.interrupts)
    ? data.interrupts
    : null;

  if (Array.isArray(interrupts)) {
    return interrupts.filter(isAgentInterrupt);
  }

  return [];
}

type ApiPagination = {
  nextCursor: string | null;
  prevCursor: string | null;
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
};

type ApiMessage = {
  id: string;
  role: string;
  content: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
};

type ApiListResponse = {
  success: boolean;
  data: ApiMessage[];
  pagination?: ApiPagination;
};

export type UseChatMessagesInfiniteResult = {
  pages: { 
    messages: ChatMessage[]; 
    tools: ChatTool[]; 
    interrupts: ChatInterrupt[];
    nextCursor: string | null; 
    hasMoreAfter: boolean 
  }[];
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  error: unknown;
};

export function useChatMessagesInfinite(opts: { userId: string | null | undefined; enabled?: boolean }): UseChatMessagesInfiniteResult {
  const enabled = !!opts.enabled && !!opts.userId;

  const normalizeToolEvent = (raw: unknown): { event: string; data: unknown } | null => {
    let x: unknown = raw;

    if (typeof x === "string") {
      try {
        x = JSON.parse(x);
      } catch {
        return null;
      }
    }

    if (!isRecord(x)) {
      return null;
    }

    // Raw shapes saved by backend (no {event,data} wrapper), e.g.:
    // - tool_message: { level, message, extra, ... }
    // - tool_progress: { progress, total, message, ... }
    if (typeof x.progress === "number" || typeof x.total === "number") {
      return { event: "tool_progress", data: x };
    }

    if (typeof x.level === "string") {
      return { event: "tool_message", data: x };
    }

    // Common shapes:
    // - { event, data }
    // - { type, data }
    // - { event, payload }
    // - { event: { event, data }, data?: ... }
    if (typeof x.event === "string") {
      return { event: x.event, data: x.data ?? x.payload ?? null };
    }

    if (typeof x.type === "string") {
      return { event: x.type, data: x.data ?? x.payload ?? null };
    }

    if (isRecord(x.event) && typeof x.event.event === "string") {
      return { event: x.event.event, data: x.event.data ?? x.data ?? null };
    }

    return null;
  };

  const query = useInfiniteQuery({
    queryKey: ["ai-chat-messages", opts.userId],
    enabled,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const apiBase = import.meta.env.VITE_API_URL as string | undefined;
      const url = new URL(joinUrl(apiBase, "/ai/chat/messages"), window.location.origin);

      url.searchParams.set("limit", "10");
      url.searchParams.set("orderBy", "-createdAt");

      if (pageParam) {
        url.searchParams.set("after", pageParam);
      }

      const res = await fetch(url.toString(), { method: "GET", credentials: "include" });

      if (!res.ok) {
        const text = await res.text().catch(() => "");

        throw new Error(`HTTP ${res.status} ${res.statusText}${text ? ` - ${text}` : ""}`);
      }

      const json: ApiListResponse = await res.json();
      const data = Array.isArray(json?.data) ? json.data : [];
      const pagination = json?.pagination;

      const messages: ChatMessage[] = [];
      const tools: ChatTool[] = [];
      const interrupts: ChatInterrupt[] = [];

      for (const m of data) {
        const role = mapApiRole(m.role);

        if (role === "tool") {
          const parsed = parseJsonContent(m.content);
          const parsedRecord = isRecord(parsed) ? parsed : {};

          const tool_start = (parsedRecord.tool_start ?? parsedRecord.toolStart) as AgentToolStartEvent["data"] | undefined;
          const tool_end = (parsedRecord.tool_end ?? parsedRecord.toolEnd) as AgentToolEndEvent["data"] | undefined;
          const eventsRaw = parsedRecord.events;
          const events = Array.isArray(eventsRaw)
            ? eventsRaw
                .map((e) => normalizeToolEvent(e))
                .filter((e): e is { event: string; data: unknown } => !!e)
            : [];

          if (!tool_start && !tool_end) {
            continue;
          }

          const toolCallId = (tool_end?.id ?? tool_start?.id ?? m.id) as string;
          const toolName = (tool_end?.name ?? tool_start?.name ?? m.metadata?.toolName ?? "tool") as string;

          // Se não existe tool_end, assumimos falha/aborto (não temos status final).
          const statusStr = String(tool_end?.status ?? "");
          const status: ChatTool["status"] =
            !tool_end
              ? "error"
              : statusStr.toLowerCase() === "interrupt"
                ? "interrupt"
                : /error|fail/i.test(statusStr)
                  ? "error"
                  : "done";

          tools.push({
            id: toolCallId,
            kind: "tool_activity",
            name: toolName,
            status,
            started_at: m.createdAt,
            ended_at: m.createdAt,
            tool_start,
            tool_end,
            events,
          });

          continue;
        }

        if (role === "interrupt") {
          if (!m.id || !m.createdAt) {
            continue;
          }

          const parsedInterrupts = parseInterrupts(m.content);
          const items = parsedInterrupts.length > 0
            ? parsedInterrupts
            : [{ action_requests: [], review_configs: [] }];

          interrupts.push(...items.map((interrupt, index) => ({
            id: index === 0 ? m.id : `${m.id}:${index}`,
            created_at: m.createdAt,
            interrupt,
            metadata: m.metadata,
          })));

          continue;
        }

        if (role === "user" || role === "assistant") {
          if (!m.id || !m.createdAt) {
            continue;
          }

          messages.push({
            id: m.id,
            role,
            content: m.content ?? "",
            created_at: m.createdAt,
            metadata: m.metadata as ChatMessage["metadata"],
          });
        }
      }
 
      /**
       * Pedimos para o back as mais recentes primeiro e depois
       * invertemos o lote para ficar em ordem cronológica.
       *
       * Status/toolStatus dos interrupts vêm direto do metadata persistido
       * pelo backend (`status`, `toolStatus`, `failureReason`) — não há
       * inferência por nome de tool no front. A correlação é determinística
       * via `toolCallId`.
       */
      messages.reverse();
      tools.reverse();
      interrupts.reverse();

      return {
        messages,
        tools,
        interrupts,
        nextCursor: (typeof pagination?.nextCursor === "string" ? pagination.nextCursor : null) as string | null,
        hasMoreAfter: !!pagination?.hasMoreAfter,
      };
    },

    getNextPageParam: (lastPage) => {
      if (!lastPage?.hasMoreAfter) {
        return undefined;
      }

      return lastPage?.nextCursor ?? undefined;
    },
  });

  return {
    pages: query.data?.pages ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: () => query.fetchNextPage(),
    error: query.error,
  };
}

